/** @jsxRuntime classic */
/*@jsx jsx*/

import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Colors } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { SingleArticleQuery_articleCollection_items_body_links_entries_block_InArticleCtaContainer_fallbackCta } from 'graphql/__generated__/operations/SingleArticleQuery';
import { useRouter } from 'next/router';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { Headline } from './CTACommonStyles';

type props = {
  inArticleCTA: SingleArticleQuery_articleCollection_items_body_links_entries_block_InArticleCtaContainer_fallbackCta;
};

export const ExploreCTA = ({ inArticleCTA }: props) => {
  const { headline, buttonText, buttonUrl, image } = inArticleCTA;
  const trackEvent = useTrackEvent();
  const router = useRouter();

  return (
    <CTAWrapper>
      <Image src={image?.url!} alt="zapier apps" />
      <ActionSection>
        <Headline>{headline}</Headline>
        <ButtonWrapper>
          <Button
            target="_blank"
            href={buttonUrl!}
            color="primary-alt"
            onClick={() => {
              trackEvent('call_to_action', {
                path: window.location.pathname,
                from_path: document.referrer,
                cta_name: 'explore_integrations',
                cta_raw_text: headline!,
                cta_location: router.asPath,
                cta_goal: 'get_more_information',
                link_destination: buttonUrl!,
              });
            }}
          >
            {buttonText}
          </Button>
        </ButtonWrapper>
      </ActionSection>
      {image && (
        <ImageBackground
          css={css`
            background-image: url(${image?.url!});
          `}
        />
      )}
    </CTAWrapper>
  );
};

const CTAWrapper = styled.article`
  border-radius: 5px;
  background-color: ${Colors.peach};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  ${mediaQueries.tablet} {
    flex-direction: row;
  }
`;

const ActionSection = styled.section`
  padding: 30px 20px;
  text-align: center;
  flex: 2;
  ${mediaQueries.tablet} {
    text-align: left;
    padding: 30px;
  }
`;

const Image = styled.img`
  width: 100%;
  ${mediaQueries.tablet} {
    display: none;
  }
`;

const ImageBackground = styled.aside`
  display: none;
  background-repeat: no-repeat;
  flex: 1;
  background-position: -25px 0px;
  background-size: 404px;
  ${mediaQueries.tablet} {
    display: block;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;

  /**
   * We had to resort to this as we're already overriding the span:hover behavior
   here https://github.com/zapier/blog/blob/f21ad3a031a3f701201b03449ed0f39d0087a89b/src/components/Link/Link.tsx#L75-L78
   */
  a:hover,
  a:focus {
    span {
      color: ${Colors.white};
    }
  }
`;
